import type { AnyFunction, FunctionArgumentsAsArray } from './types';

const hasWindow = typeof window !== 'undefined';

export const createEventEmitter = <TEvents extends Record<string, any>>(
  key: string,
  /** Throws if you try to subscribe more than once to the same event. */
  singleSubscriber?: boolean
) => {
  type ListenerMap = Map<
    AnyFunction,
    { wrapper: EventListener; subscriberKey?: string }
  >;

  const listenerMap = new Map<keyof TEvents, ListenerMap>();

  function getWindowEventName<TKey extends keyof TEvents>(event: TKey) {
    return `reckon:${key}:${String(event)}`;
  }

  function subscribe<TKey extends keyof TEvents>(
    event: TKey,
    fn: TEvents[TKey],
    subscriberKey?: string
  ) {
    let listeners = listenerMap.get(event);

    if (!listeners) {
      listeners = new Map<
        AnyFunction,
        { wrapper: EventListener; subscriberKey?: string }
      >();
      listenerMap.set(event, listeners);
    }

    if (singleSubscriber && listeners.size > 0) {
      throw Error('This event can only have 1 subscriber at a time');
    }

    const wrapper: EventListener = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      (fn as AnyFunction).apply(null, detail);
    };

    listeners.set(fn, { wrapper, subscriberKey });

    if (hasWindow) {
      window.addEventListener(getWindowEventName(event), wrapper);
    }

    return () => {
      unsubscribe(event, fn);
    };
  }

  function unsubscribe<TEventKey extends keyof TEvents>(
    event: TEventKey,
    key: AnyFunction | string
  ) {
    const listeners = listenerMap.get(event);

    if (!listeners) {
      return;
    }

    if (typeof key === 'function') {
      const entry = listeners.get(key);
      if (entry && hasWindow) {
        window.removeEventListener(getWindowEventName(event), entry.wrapper);
      }
      listeners.delete(key);
    } else if (typeof key === 'string') {
      let mapKey: AnyFunction | undefined = undefined;
      listeners.forEach((value, fnKey) => {
        if (value.subscriberKey === key) {
          mapKey = fnKey;
        }
      });
      if (mapKey) {
        const entry = listeners.get(mapKey);
        if (entry && hasWindow) {
          window.removeEventListener(getWindowEventName(event), entry.wrapper);
        }
        listeners.delete(mapKey);
      }
    }
  }

  function broadcast<TEventKey extends keyof TEvents>(
    event: TEventKey,
    payload: FunctionArgumentsAsArray<TEvents[TEventKey]>
  ) {
    if (!hasWindow) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(getWindowEventName(event), { detail: payload })
    );
  }

  return {
    broadcast,
    subscribe,
    unsubscribe,
  };
};
